import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { map, Observable, of } from 'rxjs';

import { APIResponse } from '@data-access/model';
import { IUserService, USER_SERVICE } from '@data-access/repository';

import { LOCAL_STORAGE, SESSION_STORAGE } from '@shared/constants';
import { USER_STATE_SERVICE } from '@shared/state';
import { storageManager } from '@shared/storage';
import {
  clearActiveUserId,
  getAccountByOrgId,
  getActiveUserId,
  resolveAndStoreActiveOrg,
  restoreActiveAccountUserId,
  setActiveUserId
} from '@shared/utils';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard {
  public readonly router = inject(Router);
  private userService = inject<IUserService>(USER_SERVICE);
  private userStateService = inject(USER_STATE_SERVICE);

  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    if (!this.checkIfUserLoggedIn(state.url)) {
      return of(false);
    }

    const restrictedPaths = ['organization', 'admin'];
    const path = route.routeConfig?.path || '';

    if (restrictedPaths.includes(path)) {
      return this.userService.hasSuperUserPrivileges().pipe(
        map((response: APIResponse<boolean>) => {
          const hasPrivileges = response.isSuccess() ? response.value.getValue() : false;
          if (!hasPrivileges) {
            this.router.navigate(['access-denied']);
            return false;
          }
          return true;
        })
      );
    }

    return of(true);
  }

  private checkIfUserLoggedIn(url: string): boolean {
    const orgIdFromUrl = this.getOrgIdFromUrl(url);

    if (!getActiveUserId() && !url.includes('onboarding/org/')) {
      const restoreActiveAccount = restoreActiveAccountUserId(orgIdFromUrl || 0);
      if (!restoreActiveAccount) {
        storageManager.session.set(SESSION_STORAGE.REDIRECT_URI_KEY, location.pathname + location.search);
        this.redirectToLogin();
        return false;
      }
    } else if (url.includes('onboarding/org/')) {
      this.getUserIdFromUrlAndSetActive(url);
    }

    const hasValidToken = storageManager.local.get<string>(LOCAL_STORAGE.ACCESS_TOKEN);
    if (!hasValidToken) {
      storageManager.session.set(SESSION_STORAGE.REDIRECT_URI_KEY, location.pathname + location.search);
      this.redirectToLogin();
      return false;
    }

    if (orgIdFromUrl > 0 && !url.includes('onboarding/org/')) {
      const resolved = resolveAndStoreActiveOrg(orgIdFromUrl);

      if (!resolved) {
        const account = getAccountByOrgId(orgIdFromUrl);
        if (!account) {
          this.router.navigate(['not-found']);
          return false;
        }
        setActiveUserId(account.userId);
        const resolvedAfterSwitch = resolveAndStoreActiveOrg(orgIdFromUrl);
        if (!resolvedAfterSwitch) {
          this.router.navigate(['not-found']);
          return false;
        }
        const switchedTokenSuccess = storageManager.local.get<string>(LOCAL_STORAGE.ACCESS_TOKEN);

        if (!switchedTokenSuccess) {
          storageManager.session.set(SESSION_STORAGE.REDIRECT_URI_KEY, location.pathname + location.search);
          this.redirectToLogin();
          return false;
        }
      }
    }

    return true;
  }

  private getOrgIdFromUrl(url: string): number {
    const parts = url.split('/').filter(Boolean);
    const orgIndex = parts.indexOf('org');
    if (orgIndex < 0) {
      return 0;
    }
    const orgId = Number(parts[orgIndex + 1]);
    return orgId > 0 ? orgId : 0;
  }

  private redirectToLogin() {
    clearActiveUserId();
    this.userStateService.clearActiveOrg();
    this.router.navigate(['auth/login']);
  }

  private getUserIdFromUrlAndSetActive(url: string): void {
    const parts = url.split('/').filter(Boolean);
    const userIndex = parts.indexOf('user');
    if (userIndex < 0) {
      return;
    }
    const userId = Number(parts[userIndex + 1]);
    if (userId > 0) {
      setActiveUserId(userId);
    }
  }
}
